// client/src/components/plataforma/users/tabs/UserProfileTab.js
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../../../context/auth/AuthProvider';
import { 
    User, Mail, Phone, Camera, AlertCircle, Briefcase, Loader2, Shield, Eye, UploadCloud, 
    Lock, Trash2, X, Save, Settings, XCircle
} from 'lucide-react';

import ImageZoomModal from '../../../common/ImageZoomModal';
import PlaceholderGenerator from '../../../../utils/placeholders';

const UserProfileTab = ({ 
    formData = {},
    onChange, 
    isEditing = false,
    isCreating = false,
    isLoading = false,
    isUploadingPhoto = false,
    formErrors = {},
    onSavePhoto,
    onRemovePhoto,
    canManagePhoto = false,
    isOwnProfile = false,
    onSave, 
    isSaving = false 
}) => {
    const { user: currentUser } = useAuth();
    const safeFormData = formData || {};
    
    const [localIsEditing, setLocalIsEditing] = useState(isCreating); 
    
    const isInteractable = localIsEditing || isCreating;
    const effectiveCanManagePhoto = isCreating ? true : canManagePhoto;
    
    const [photoPreview, setPhotoPreview] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [file, setFile] = useState(null);
    const [zoomImage, setZoomImage] = useState(null);
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const [showIniciais, setShowIniciais] = useState(true);
    const [iniciais, setIniciais] = useState('');
    const [imgError, setImgError] = useState(false);
    const [showPermissionWarning, setShowPermissionWarning] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    const isProfissional = useMemo(() => {
        if (isCreating) {
            return safeFormData.isProfissional || false;
        }
        return !!safeFormData.profissionalId || safeFormData.isProfissional;
    }, [isCreating, safeFormData.isProfissional, safeFormData.profissionalId]);

    const getPhotoUrl = useCallback(() => {
        if (photoPreview) return photoPreview;
        const photo = (safeFormData.photoUrl || safeFormData.photo || '');
        if (photo && typeof photo === 'string' && photo.length > 5 && !imgError) {
             return photo;
        }
        return null;
    }, [photoPreview, safeFormData, imgError]);

    useEffect(() => {
        const nomeCompleto = safeFormData.name || 'Usuário';
        const email = safeFormData.email || '';
        
        const generatedInitials = PlaceholderGenerator.getInitials(nomeCompleto, email);
        setIniciais(generatedInitials);

        if (getPhotoUrl()) {
            setShowIniciais(false);
        } else {
            setShowIniciais(true);
        }
    }, [safeFormData.name, safeFormData.email, getPhotoUrl]);

    const handleNestedChange = useCallback((section, field, value) => {
        if (!onChange || !isInteractable) return;
        
        const currentData = JSON.parse(JSON.stringify(safeFormData));
        if (!currentData[section]) currentData[section] = {};
        
        const parts = field.includes('.') ? field.split('.') : [field];
        let currentLevel = currentData[section];
        
        for (let i = 0; i < parts.length - 1; i++) {
            if (!currentLevel[parts[i]]) currentLevel[parts[i]] = {};
            currentLevel = currentLevel[parts[i]];
        }
        
        currentLevel[parts[parts.length - 1]] = value;
        onChange({ target: { name: 'nestedUpdate', value: currentData } });
        
    }, [safeFormData, onChange, isInteractable]);

    const handleSimpleChange = useCallback((event) => {
        if (!onChange || !isInteractable) return;
        const { name, value, type, checked } = event.target;
        const finalValue = type === 'checkbox' ? checked : value;
        onChange({ target: { name, value: finalValue } });
    }, [onChange, isInteractable]);

    const handleFileChange = useCallback((event) => {
        if (!effectiveCanManagePhoto) {
            setShowPermissionWarning(true);
            setTimeout(() => setShowPermissionWarning(false), 3000);
            event.target.value = '';
            return;
        }

        const selectedFile = event.target.files[0];
        if (!selectedFile) return;
        
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(selectedFile.type)) {
            setUploadError('Tipo de arquivo não suportado. Use JPG, PNG, WEBP ou GIF.');
            return;
        }
        
        if (selectedFile.size > 5 * 1024 * 1024) {
             setUploadError('A imagem deve ter no máximo 5MB.');
             return;
        }

        setUploadError(null);
        setImgError(false);
        setFile(selectedFile);
        setSelectedFile(selectedFile);
        
        const previewUrl = URL.createObjectURL(selectedFile);
        setPhotoPreview(previewUrl);
        setShowIniciais(false);

        if (onChange) {
            onChange({ target: { name: 'photoFile', value: selectedFile } });
        }
        
        event.target.value = '';
    }, [effectiveCanManagePhoto, onChange]);

    const handleUploadClick = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!effectiveCanManagePhoto) {
            setUploadError('Você não tem permissão para atualizar esta foto');
            setShowPermissionWarning(true);
            setTimeout(() => setShowPermissionWarning(false), 3000);
            return;
        }
        
        if (!file || !onSavePhoto) {
            setUploadError('Selecione um arquivo primeiro.');
            return;
        }
        
        setUploadError(null);
        
        try {
            await onSavePhoto(file);
            setFile(null);
            setSelectedFile(null);
            setPhotoPreview(null);
            setShowIniciais(true);
            
            if (photoPreview) URL.revokeObjectURL(photoPreview);
        } catch (err) {
            setUploadError('Falha ao enviar foto.');
        }
    }, [effectiveCanManagePhoto, file, onSavePhoto, photoPreview]);

    const handleCancelPhoto = useCallback(() => {
        if (photoPreview) URL.revokeObjectURL(photoPreview);
        setPhotoPreview(null);
        setFile(null);
        setSelectedFile(null);
        setUploadError(null);
        setShowIniciais(true);
        
        const input = document.getElementById('fotoPerfilInputUser');
        if (input) input.value = '';
    }, [photoPreview]);

    const handleRemovePhotoClick = useCallback(() => {
        if (!effectiveCanManagePhoto) {
            setUploadError('Você não tem permissão para remover esta foto');
            setShowPermissionWarning(true);
            setTimeout(() => setShowPermissionWarning(false), 3000);
            return;
        }
        
        if (typeof onRemovePhoto !== 'function') {
            setUploadError('Função de remoção não disponível');
            return;
        }

        if (window.confirm('Tem certeza que deseja remover esta foto?')) {
            onRemovePhoto()
                .then(() => {
                    if (photoPreview) URL.revokeObjectURL(photoPreview);
                    setPhotoPreview(null);
                    setFile(null);
                    setSelectedFile(null);
                    setShowIniciais(true);
                    setImgError(false);
                    setUploadError(null);
                    if (onChange) {
                        onChange({ target: { name: 'photo', value: '' } });
                        onChange({ target: { name: 'photoUrl', value: '' } });
                        onChange({ target: { name: 'photoFile', value: null } });
                    }
                })
                .catch((err) => {
                    setUploadError(err.response?.data?.error || err.response?.data?.message || 'Erro ao remover foto');
                });
        }
    }, [effectiveCanManagePhoto, onRemovePhoto, photoPreview, onChange]);

    const handleZoomFoto = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const currentUrl = getPhotoUrl();
        if (!currentUrl || showIniciais) return;
        
        setZoomImage({
            url: currentUrl,
            nomeOriginal: `Foto de ${safeFormData.name || 'Usuário'}`
        });
        setIsZoomOpen(true);
    }, [getPhotoUrl, showIniciais, safeFormData]);

    const handleSelectImageClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!effectiveCanManagePhoto) {
            setShowPermissionWarning(true);
            setTimeout(() => setShowPermissionWarning(false), 3000);
            return;
        }
        
        const input = document.getElementById('fotoPerfilInputUser');
        if (input) input.click();
    }, [effectiveCanManagePhoto]);

    const formatDateForInput = useCallback((dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return !isNaN(date.getTime()) ? date.toISOString().split('T')[0] : '';
        } catch (e) { 
            return ''; 
        }
    }, []);

    useEffect(() => {
        return () => {
            if (photoPreview) URL.revokeObjectURL(photoPreview);
        };
    }, [photoPreview]);

    const handleCancel = () => {
        setLocalIsEditing(false);
    };

    const handleSaveSubmit = () => {
        if (onSave) {
            onSave();
            setLocalIsEditing(false);
        }
    };

    if (isLoading) {
        return (
            <div className="table-loading-state">
                <Loader2 size={24} className="animate-spin" />
                <span>Carregando dados do usuário...</span>
            </div>
        );
    }

    const metadata = safeFormData.metadata || {
        tipoUsuario: 'interno',
        notificacoes: { email: true, sms: false, push: true }
    };
    
    const nomeExibicao = safeFormData.name || 'Usuário';
    const hasPhoto = getPhotoUrl() && !showIniciais;
    const hasSelectedFile = !!selectedFile;

    return (
        <div className="form-content">
            <div className="form-header flex items-center justify-between mb-6">
                <div>
                    <h3 className="form-title flex items-center mb-0">
                        <User size={20} className="mr-2 text-primary" />
                        Dados Pessoais
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        {isCreating 
                            ? 'Preencha os dados básicos do usuário. Telefone e Nome são obrigatórios.'
                            : 'Gerencie as informações pessoais do perfil.'}
                    </p>
                </div>

                {!isCreating && canManagePhoto && (
                    !localIsEditing ? (
                        <button 
                            type="button" 
                            onClick={() => setLocalIsEditing(true)} 
                            className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                        >
                            <Settings size={16} className="mr-2 text-gray-500" /> Editar Perfil
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button 
                                type="button" 
                                onClick={handleCancel} 
                                className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                            >
                                <XCircle size={16} className="mr-2 text-gray-500" /> Cancelar
                            </button>
                            <button 
                                type="button" 
                                onClick={handleSaveSubmit} 
                                disabled={isSaving} 
                                className="action-button action-button-primary bg-primary text-white shadow-sm"
                            >
                                {isSaving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                                Salvar Perfil
                            </button>
                        </div>
                    )
                )}
            </div>

            {showPermissionWarning && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg animate-pulse">
                    <div className="flex items-start gap-3">
                        <Lock className="text-yellow-600 mt-0.5" size={20} />
                        <div>
                            <h4 className="text-sm font-medium text-yellow-800">Permissão Negada</h4>
                            <p className="text-sm text-yellow-700 mt-1">
                                {isCreating 
                                    ? 'Modo criação - permissões completas'
                                    : (isOwnProfile 
                                        ? 'Você não tem permissão para gerenciar sua foto'
                                        : 'Apenas administradores matriz podem gerenciar fotos de outros usuários'
                                      )
                                }
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="form-grid-container items-start">
                <div className="form-section flex flex-col gap-4">
                    <h4 className="form-section-title">
                        <Camera size={16} className="inline mr-2"/>
                        Foto de Perfil
                    </h4>
                    
                    <div className="flex flex-col items-center gap-4 bg-white p-6 rounded-lg border border-gray-200">
                        <div className="relative group">
                            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-md bg-gray-100 relative flex items-center justify-center">
                                {!showIniciais && getPhotoUrl() ? (
                                    <div className="w-full h-full relative group">
                                        <img 
                                            src={getPhotoUrl()} 
                                            alt={`Foto de ${nomeExibicao}`} 
                                            className="w-full h-full object-cover cursor-pointer" 
                                            onClick={handleZoomFoto}
                                            onError={(e) => {
                                                setImgError(true);
                                                setShowIniciais(true);
                                                e.target.onerror = null;
                                            }}
                                        />
                                        <div 
                                            className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                                            onClick={handleZoomFoto}
                                        >
                                            <Eye className="text-white" size={24} />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-full h-full bg-primary text-white flex items-center justify-center text-3xl font-bold select-none">
                                        {iniciais}
                                    </div>
                                )}

                                {isUploadingPhoto && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
                                        <Loader2 className="animate-spin text-white" size={24} />
                                    </div>
                                )}
                            </div>
                            
                            {!effectiveCanManagePhoto && !isInteractable && (
                                <div className="absolute -top-2 -right-2">
                                    <Lock className="text-gray-400" size={16} />
                                </div>
                            )}
                        </div>

                        {effectiveCanManagePhoto && isInteractable ? (
                            <div className="flex flex-col items-center gap-2 w-full">
                                <input 
                                    type="file" 
                                    id="fotoPerfilInputUser" 
                                    accept="image/*" 
                                    onChange={handleFileChange} 
                                    style={{ display: 'none' }} 
                                    disabled={isUploadingPhoto || !isInteractable}
                                />
                                
                                <div className="flex gap-2 w-full">
                                    {!hasSelectedFile ? (
                                        <button 
                                            type="button"
                                            onClick={handleSelectImageClick}
                                            className="action-button action-button-secondary w-full justify-center text-sm"
                                            disabled={isUploadingPhoto || !isInteractable}
                                        >
                                            <Camera size={16} className="mr-2"/>
                                            Selecionar Imagem
                                        </button>
                                    ) : (
                                        <>
                                            <button 
                                                type="button"
                                                onClick={handleUploadClick}
                                                disabled={isUploadingPhoto || !isInteractable}
                                                className="action-button action-button-primary flex-1 justify-center text-sm"
                                            >
                                                {isUploadingPhoto ? (
                                                    <><Loader2 size={16} className="animate-spin mr-1"/>Enviando...</>
                                                ) : (
                                                    <><UploadCloud size={16} className="mr-1"/>Enviar</>
                                                )}
                                            </button>
                                            
                                            <button 
                                                type="button"
                                                onClick={handleCancelPhoto}
                                                className="action-button action-button-secondary flex-1 justify-center text-sm"
                                                disabled={isUploadingPhoto || !isInteractable}
                                            >
                                                <X size={16} className="mr-1"/>Cancelar
                                            </button>
                                        </>
                                    )}
                                </div>

                                {hasPhoto && !hasSelectedFile && (
                                    <button 
                                        type="button" 
                                        onClick={handleRemovePhotoClick} 
                                        className="text-xs text-red-600 hover:text-red-800 hover:underline flex items-center mt-1"
                                        disabled={isUploadingPhoto || !isInteractable}
                                    >
                                        <Trash2 size={14} className="mr-1" />
                                        Remover foto
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="text-center text-sm text-gray-500 mt-2">
                                {isCreating 
                                    ? 'Selecione uma foto para o novo usuário'
                                    : (isOwnProfile 
                                        ? 'Use "Meu Perfil" para atualizar sua foto'
                                        : 'Apenas administradores matriz podem gerenciar esta foto'
                                      )
                                }
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-section flex flex-col gap-4">
                    <h4 className="form-section-title">
                        <User size={16} className="inline mr-2"/>
                        Dados Pessoais
                    </h4>
                    
                    <div className="form-grid-container">
                        <div className="form-group full-width">
                            <label className="required">Nome Completo</label>
                            <input 
                                type="text" 
                                name="name" 
                                value={safeFormData.name || ''} 
                                onChange={handleSimpleChange} 
                                required 
                                className={`form-input ${formErrors.name ? 'border-red-500' : ''} ${!isInteractable ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                placeholder="Ex: João da Silva" 
                                disabled={!isInteractable || isUploadingPhoto}
                                readOnly={!isInteractable}
                            />
                            {formErrors.name && <div className="form-error-message">{formErrors.name}</div>}
                        </div>
                        
                        <div className="form-group full-width">
                            <label>Email</label>
                            <div className="relative">
                                <Mail size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input 
                                    type="email" 
                                    name="email" 
                                    value={safeFormData.email || ''} 
                                    onChange={handleSimpleChange} 
                                    className={`form-input pl-10 ${!isInteractable ? 'bg-gray-50 text-gray-600' : 'bg-white'} ${formErrors.email ? 'border-red-500' : ''}`}
                                    placeholder="email@exemplo.com" 
                                    disabled={!isInteractable || isUploadingPhoto}
                                    readOnly={!isInteractable}
                                />
                            </div>
                            {formErrors.email && <div className="form-error-message">{formErrors.email}</div>}
                        </div>
                        
                        <div className="form-group">
                            <label className="required">Telefone</label>
                            <div className="relative">
                                <Phone size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input 
                                    type="tel" 
                                    name="telefone" 
                                    value={safeFormData.telefone || ''} 
                                    onChange={handleSimpleChange} 
                                    required 
                                    className={`form-input pl-10 ${!isInteractable ? 'bg-gray-50 text-gray-600' : 'bg-white'} ${formErrors.telefone ? 'border-red-500' : ''}`}
                                    placeholder="(00) 00000-0000" 
                                    disabled={!isInteractable || isUploadingPhoto}
                                    readOnly={!isInteractable}
                                />
                            </div>
                            {formErrors.telefone && <div className="form-error-message">{formErrors.telefone}</div>}
                        </div>
                        
                        <div className="form-group">
                            <label>CPF</label>
                            <input 
                                type="text" 
                                name="cpf" 
                                value={safeFormData.cpf || ''} 
                                onChange={handleSimpleChange} 
                                className={`form-input ${!isInteractable ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                placeholder="000.000.000-00" 
                                disabled={!isInteractable || isUploadingPhoto}
                                readOnly={!isInteractable}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Data de Nascimento</label>
                            <input 
                                type="date" 
                                name="dataNascimento" 
                                value={formatDateForInput(safeFormData.dataNascimento)} 
                                onChange={handleSimpleChange} 
                                className={`form-input ${!isInteractable ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                disabled={!isInteractable || isUploadingPhoto}
                                readOnly={!isInteractable}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Tipo de Usuário</label>
                            <select 
                                value={metadata.tipoUsuario || 'interno'} 
                                onChange={(e) => handleNestedChange('metadata', 'tipoUsuario', e.target.value)} 
                                className={`form-input ${!isInteractable ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                disabled={!isInteractable || isUploadingPhoto}
                            >
                                <option value="interno">👨‍💼 Interno (Colaborador)</option>
                                <option value="externo">👥 Externo (Cliente/Fornecedor)</option>
                                <option value="sistema">🤖 Sistema (Integrações)</option>
                            </select>
                        </div>

                        {/* ✅ REMOVIDO CHECKBOX DE STATUS: FOI MOVIDO PARA A ABA DE PERMISSÕES! */}
                    </div>
                </div>
            </div>

            {/* SEÇÃO PROFISSIONAL */}
            <div className="form-section mt-6">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="form-section-title">
                        <Briefcase size={16} className="inline mr-2"/>
                        Configuração Profissional
                    </h4>
                </div>
                <div className={`p-4 rounded-lg border transition-colors ${isProfissional ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                    <label className={`checkbox-label flex items-start gap-3 ${!isInteractable ? 'cursor-default' : 'cursor-pointer'}`}>
                        <input 
                            type="checkbox" 
                            name="isProfissional"
                            checked={isProfissional} 
                            onChange={(e) => handleSimpleChange({
                                target: {
                                    name: 'isProfissional',
                                    value: e.target.checked,
                                    type: 'checkbox',
                                    checked: e.target.checked
                                }
                            })}
                            className="form-checkbox mt-1 w-5 h-5"
                            disabled={!isInteractable || isUploadingPhoto}
                        />
                        <div>
                            <span className={`font-medium block ${isProfissional ? 'text-blue-900' : 'text-gray-800'}`}>
                                Habilitar como Profissional
                            </span>
                            <span className={`text-sm mt-1 block ${isProfissional ? 'text-blue-700' : 'text-gray-500'}`}>
                                Marque esta opção se este usuário realizará agendamentos e atendimentos no sistema.
                                Mais detalhes poderão ser configurados na aba "Profissional".
                            </span>
                        </div>
                    </label>
                </div>
            </div>

            {isOwnProfile && isInteractable && !isCreating && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="text-yellow-600 mt-0.5" size={20} />
                        <div>
                            <h4 className="text-sm font-medium text-yellow-800">Edição Limitada</h4>
                            <p className="text-sm text-yellow-700 mt-1">
                                Você está editando seu próprio perfil. Configurações de permissão devem ser alteradas na aba apropriada.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            
            <ImageZoomModal
                image={zoomImage}
                isOpen={isZoomOpen}
                onClose={() => setIsZoomOpen(false)}
            />
        </div>
    );
};

export default UserProfileTab;